import type { KeyValueStorage } from '../ports/storage.ts';
import { createTracker } from './tracker.ts';
import type { Tracker, TrackSend, TrackTimers } from './tracker.ts';

/**
 * Browser binding of the tracker. Everything is read from `globalThis` loosely (this package has no
 * DOM lib); a missing API means "not supported" and the tracker degrades to a no-op or a weaker path.
 */

type Loose = Record<string, unknown>;
type Method = (...args: unknown[]) => unknown;

function read(source: unknown, key: string): unknown {
  try {
    return typeof source === 'object' && source !== null ? (source as Loose)[key] : undefined;
  } catch {
    return undefined;
  }
}

function method(owner: unknown, name: string): Method | null {
  const value = read(owner, name);
  return typeof value === 'function' ? (value as Method) : null;
}

/** Do Not Track (`'1'`/`'yes'`) or Global Privacy Control set: the visitor opted out. */
export function isOptedOut(nav: unknown, win: unknown): boolean {
  const dnt = [read(nav, 'doNotTrack'), read(win, 'doNotTrack'), read(nav, 'msDoNotTrack')];
  return dnt.some((v) => v === '1' || v === 'yes') || read(nav, 'globalPrivacyControl') === true;
}

/** `localStorage` that never throws: in memory when blocked. */
function browserStorage(): KeyValueStorage {
  const memory = new Map<string, string>();
  const local = (): unknown => read(globalThis, 'localStorage');
  return {
    get(key) {
      try {
        const value = method(local(), 'getItem')?.call(local(), key);
        return typeof value === 'string' ? value : (memory.get(key) ?? null);
      } catch {
        return memory.get(key) ?? null;
      }
    },
    set(key, value) {
      memory.set(key, value);
      try {
        method(local(), 'setItem')?.call(local(), key, value);
      } catch {
        // Stays in memory.
      }
    },
    remove(key) {
      memory.delete(key);
      try {
        method(local(), 'removeItem')?.call(local(), key);
      } catch {
        // Nothing durable to remove.
      }
    },
  };
}

const timers: TrackTimers = {
  set: (callback, ms) => method(globalThis, 'setTimeout')?.call(globalThis, callback, ms),
  clear: (handle) => method(globalThis, 'clearTimeout')?.call(globalThis, handle),
};

/** `navigator.sendBeacon` first (survives page unload), else `fetch` with `keepalive`. */
export function browserSend(): TrackSend {
  return (url, body) => {
    const nav = read(globalThis, 'navigator');
    const BlobCtor = read(globalThis, 'Blob') as
      (new (parts: string[], init: object) => unknown) | undefined;
    const beacon = method(nav, 'sendBeacon');
    if (beacon !== null && BlobCtor !== undefined) {
      try {
        if (beacon.call(nav, url, new BlobCtor([body], { type: 'application/json' })) === true)
          return;
      } catch {
        // Fall back to fetch.
      }
    }
    try {
      const pending = method(globalThis, 'fetch')?.call(globalThis, url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      });
      if (pending instanceof Promise) pending.catch(() => undefined);
    } catch {
      // Analytics only.
    }
  };
}

export interface WebTrackerOptions {
  endpoint: string;
  /** Default: `localStorage`, in memory when blocked. */
  storage?: KeyValueStorage;
}

/** A tracker bound to the browser: DNT/GPC and `navigator.onLine` respected, flushed when the page hides. */
export function createWebTracker(options: WebTrackerOptions): Tracker {
  const win: unknown = globalThis;
  const nav = (): unknown => read(win, 'navigator');
  const tracker = createTracker({
    endpoint: options.endpoint,
    storage: options.storage ?? browserStorage(),
    now: () => Date.now(),
    random: () => {
      const crypto = read(win, 'crypto');
      const words = method(crypto, 'getRandomValues')?.call(crypto, new Uint32Array(1));
      return words instanceof Uint32Array ? (words[0] ?? 0) / 2 ** 32 : Math.random();
    },
    send: browserSend(),
    timers,
    enabled: () => !isOptedOut(nav(), win),
    online: () => read(nav(), 'onLine') !== false,
  });
  method(win, 'addEventListener')?.call(win, 'pagehide', tracker.flush);
  const doc = read(win, 'document');
  method(doc, 'addEventListener')?.call(doc, 'visibilitychange', () => {
    if (read(doc, 'visibilityState') === 'hidden') tracker.flush();
  });
  return tracker;
}
